import React, {Fragment, useMemo} from 'react';

import {createSelector} from '@reduxjs/toolkit';
import cs from 'classnames';
import {PopoverBody} from 'reactstrap';
import styled from 'styled-components';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {useCurrentUserState} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {
  useBulkAddToFolder,
  useBulkRemoveFromFolder,
  useDocuments,
} from 'app/documents/api';
import {StyledPopoverBottomDIV} from 'app/general/bootstrap/utils';
import {PopoverForm, PopoverHeading} from 'app/general/overlays';
import StateButton from 'app/general/widgets/StateButton';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';

import {CreateFolderLink} from './dashboards/CreateFolderLink';
import {FolderSelection} from './FolderSelection';

import type {OverlayProps} from 'app/general/overlays';
import type {
  BaseDocumentContentType,
  DocumentSerializedType,
  FolderSerializedType,
  ListResponseType,
} from 'sweet-types';

interface GroupedConnection {
  id: number;
  hashid: string;
  name: string;
  connection: Array<DocumentSerializedType>;
}

interface AddToFolderItemType {
  object_id: string | number;
  document_content_type: BaseDocumentContentType;
  url: string;
}
interface Props {
  overlayProps: OverlayProps;
  onSuccess: () => Promise<any>;
  currentFolder?: string | void;
  documents: Array<AddToFolderItemType>;
}

interface ConnectionsFromResultType {
  data?: ListResponseType<DocumentSerializedType>;
}

export const AddToFolderPopover = ({
  overlayProps,
  documents,
  onSuccess,
}: Props): JSX.Element => {
  // all the connections between document and folder.
  // we filter out the one connection that can have folder=None.

  const {connections} = useDocuments(
    {
      page: '1',
      page_size: `${DEFAULT_PAGE_SIZE}`,
      document_url: documents.map((doc) => doc.url).join(','),
      folder: '',
    },
    {
      selectFromResult: (result) => ({
        connections: selectConnectionsToFoldersFromResult(result),
      }),
    }
  );

  const {isManager} = getCurrentUserPerms(useCurrentUserState().data!);

  const [bulkAddToFolder] = useBulkAddToFolder();
  const [bulkRemoveFromFolder] = useBulkRemoveFromFolder();

  const attachedFolderHashids =
    connections == null ? [] : connections.map((c) => c.folder?.hashid);

  function doAddToFolder(folder: FolderSerializedType): Promise<unknown> {
    return bulkAddToFolder({
      documents: documents.map((doc) => ({
        object_id: doc.object_id,
        document_content_type: doc.document_content_type,
        folder: folder.url,
        documentURL: doc.url,
      })),
    })
      .unwrap()
      .then(onSuccess);
  }

  const connectionsById: Array<GroupedConnection> = useMemo(() => {
    return Object.values(
      connections?.reduce((acc, item) => {
        if (item.folder == null) return acc;

        const {id, hashid, name} = item.folder;

        acc[id] =
          acc[id] ?? ({id, hashid, name, connection: []} as GroupedConnection);
        acc[id].connection.push(item);

        return acc;
      }, {}) ?? {}
    );
  }, [connections]);

  function doRemoveFromFolder(
    connection: Array<DocumentSerializedType>
  ): Promise<null> {
    return bulkRemoveFromFolder({
      documents: connection.map((doc) => doc.url),
    })
      .unwrap()
      .then(onSuccess);
  }

  function actionsGetter(
    folder: FolderSerializedType,
    afterActions?: () => Promise<any>
  ) {
    return [
      {
        text: gt('Add to this folder'),
        key: 'add-to-folder',
        icon: 'fas fa-plus',
        condition: () => !attachedFolderHashids.includes(folder.hashid),
        callback: () => {
          const addToFolderPromise = doAddToFolder(folder);
          if (afterActions == null) {
            return addToFolderPromise;
          } else {
            return addToFolderPromise.then(afterActions);
          }
        },
      },
    ];
  }

  return (
    <PopoverForm overlayProps={overlayProps}>
      {({close, refreshPosition}) => {
        return (
          <Fragment>
            {connectionsById.length > 0 ? (
              <Fragment>
                <PopoverHeading className="popover-header d-flex align-items-center justify-content-between">
                  <span>{gt('Folders')}</span>
                  <button
                    onClick={close}
                    className="btn btn-link btn-sm text-secondary"
                  >
                    <i className="fa fa-times" />
                  </button>
                </PopoverHeading>
                <ul
                  className="list-group list-group-flush flex-grow-1 flex-shrink-0 overflow-auto"
                  style={{
                    maxHeight: 'calc(60vh - 230px)',
                  }}
                >
                  {connectionsById.map((folder) => (
                    <li
                      key={folder.hashid}
                      style={{
                        borderColor: 'rgba(255,255,255,0.5)',
                      }}
                      className={cs(
                        'active list-group-item py-1 d-flex align-items-center ' +
                          'justify-content-between pr-0'
                      )}
                    >
                      <span>{folder.name}</span>
                      <LazyTooltip
                        placement="left"
                        tooltip={gt('Remove from this folder')}
                      >
                        <StateButton
                          className="btn-sm btn-link py-1 px-3 text-white"
                          onClick={() => doRemoveFromFolder(folder.connection)}
                        >
                          <i className="fa fa-fw fa-times" />
                        </StateButton>
                      </LazyTooltip>
                    </li>
                  ))}
                </ul>
              </Fragment>
            ) : null}
            <PopoverBody className="m-0 p-0 ">
              <StyledScrollableContainerDiv className="d-flex flex-column">
                <PopoverHeading className="d-flex align-items-center justify-content-between">
                  <span>{gt('Add to folder')}</span>
                  {connectionsById.length === 0 && (
                    <button
                      onClick={close}
                      className="btn btn-link btn-sm text-secondary"
                    >
                      <i className="fa fa-times" />
                    </button>
                  )}
                </PopoverHeading>
                <FolderSelection
                  actionsGetter={actionsGetter}
                  refreshPosition={refreshPosition}
                />
              </StyledScrollableContainerDiv>
              <CreateFolderLink
                autoFocus={true}
                parent={null}
                canEdit={isManager}
              />
            </PopoverBody>
            <StyledPopoverBottomDIV>
              <button onClick={close} className="btn btn-link btn-sm">
                <i className="fa fa-times" /> {gt('Close')}
              </button>
            </StyledPopoverBottomDIV>
          </Fragment>
        );
      }}
    </PopoverForm>
  );
};

const selectConnectionsToFoldersFromResult = createSelector(
  (res: ConnectionsFromResultType) => res.data,
  (data) => data?.results.filter((c) => c.folder != null)
);

const StyledScrollableContainerDiv = styled.div`
  max-height: calc(100vh - 200px);
  width: 274px;
`;
